// Render-time backstop for model-authored free-text (export_screening summary/explanation, etc.).
// The export workflow's threat model occasionally types stray XML / tool-call markup and internal
// snake_case field names INTO a structured string field (e.g. "…preliminary stage.</explanation>
// </invoke>", or "iuu_fishing_risk_index and organized_crime_index"). The backend sanitizes new runs
// at _build_export_threat_ui, but ALREADY-PERSISTED runs still carry the leak in saved state — so we
// also clean at render time here. Keep this map in sync with pyapi _LEAKED_FIELD_NAMES.

// Anchored so a real "<"/"</" must be followed by a letter or "!" — regulatory math like
// "voltage > 5V" or "<5 nm" is preserved; only actual tags/tool-call envelopes are stripped.
const MODEL_MARKUP_RE = /<\/?[A-Za-z!][\w:.-]*(?:\s[^<>]*?)?\/?>/g;

// Longest-first so the _2021/_2023 variants map before the base token.
const LEAKED_FIELD_NAMES: Array<[RegExp, string]> = [
  [/\bdoe_missing_information\b/gi, 'DOE missing-information notes'],
  [/\bdoe_assumptions\b/gi, 'DOE assumptions'],
  [/\bdoe_controlled\b/gi, 'DOE result'],
  [/\bdoe_reason\b/gi, 'DOE reason'],
  [/\bdoe_notes\b/gi, 'DOE notes'],
  [/\btranshipment_points_russia_belarus\b/gi, 'Russia/Belarus transshipment risk'],
  [/\btranshipment_points_china\b/gi, 'China transshipment risk'],
  [/\bsanctions_programs_and_country_info\b/gi, 'sanctions-program information'],
  [/\borganized_crime_index_2021\b/gi, 'organized-crime index (2021)'],
  [/\borganized_crime_index_2023\b/gi, 'organized-crime index (2023)'],
  [/\borganized_crime_index\b/gi, 'organized-crime index'],
  [/\beu_list_non_cooperative_tax\b/gi, 'EU non-cooperative-tax listing'],
  [/\biuu_fishing_risk_index\b/gi, 'illegal-fishing (IUU) risk index'],
  [/\bhigh_risk_aml_cft\b/gi, 'high AML/CFT risk'],
  [/\bhas_any_country_risk_hit\b/gi, 'country-risk screening hit'],
  [/\bknown_iwt_hubs\b/gi, 'wildlife-trafficking hub listing'],
  [/\bembargoed_countries\b/gi, 'embargo status'],
  // v2 conditional-routing (NRC/ITAR/EAR) tokens. Longest-first; only underscore tokens are
  // mapped — bare values ("exclusive", "partial", "unresolved") are normal English.
  [/\bcontinued_multi_regime\b/gi, 'continued (multiple regimes may apply)'],
  [/\bcontinued_unresolved\b/gi, 'continued (jurisdiction unresolved)'],
  [/\bjurisdiction_routing\b/gi, 'jurisdiction routing'],
  [/\bunresolved_regimes\b/gi, 'unresolved regimes'],
  [/\bexclusive_regimes\b/gi, 'exclusively controlled regimes'],
  [/\bflagged_regimes\b/gi, 'flagged regimes'],
  [/\bcoverage_status\b/gi, 'jurisdiction coverage'],
  [/\bnot_applicable\b/gi, 'not applicable'],
  [/\bnot_exclusive\b/gi, 'not exclusive'],
];

/**
 * Strip stray model markup + humanize leaked internal field tokens from a user-facing string.
 * No-op for non-strings (returns the input unchanged) so it is safe to wrap any value.
 */
export const sanitizeModelText = <T>(value: T): T => {
  if (typeof value !== 'string' || !value) {
    return value;
  }
  let out = value.replace(MODEL_MARKUP_RE, '');
  for (const [re, readable] of LEAKED_FIELD_NAMES) {
    out = out.replace(re, readable);
  }
  out = out
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\s+([.,;:)])/g, '$1')
    .trim();
  return out as unknown as T;
};

export default sanitizeModelText;
